/**
 * corruptStoreRecovery — detect and repair a corrupt local Yjs replica.
 *
 * The per-project IndexedDB database (`martini-${projectId}`) is a cache of
 * the collaborative doc; the authoritative copy lives on PartyKit. A damaged
 * store (observed in production: `Y.applyUpdate` throwing during y-indexeddb's
 * replay, hundreds of times per session for the affected user) can never be
 * repaired by app code — the fix is to wipe the cache and resync fresh from
 * the server. Updates that fail to replay are unusable bytes, so the wipe
 * loses nothing a healthy session could have kept.
 *
 * Flow:
 *  1. Detection (during bootstrap): `whenSynced` rejection, the persistence
 *     'error' event, or an uncaught error matching the known corruption
 *     signatures inside the sync window → mark the project corrupt +
 *     telemetry, and reload once (the in-memory doc may hold a partial
 *     transaction, so a clean boot is the safe restart).
 *  2. Repair (next boot): `wipeLocalStoreIfMarkedCorrupt` runs before the
 *     persistence is constructed — deletes the database, sets a 24h
 *     wipe-guard so a deeper fault (e.g. failing disk) can't wipe-loop, and
 *     lets bootstrap continue with a fresh store.
 *
 * A false-positive detection costs one cache wipe + one doc re-download —
 * benign, and bounded to once per project per 24h by the guard.
 */

import { captureSentryError } from '@/lib/sentry'
import { trackCanvasLocalDocReset } from '@/lib/analytics'

const CORRUPT_MARKER_PREFIX = 'martini_corrupt_idb:'
const WIPE_GUARD_PREFIX = 'martini_idb_wipe_guard:'
const RELOAD_GUARD_PREFIX = 'martini_idb_corrupt_reload:'

/** Only one wipe per project per this window — a store that is corrupt again
 *  immediately after a wipe points at a fault below us (disk, browser bug),
 *  where wiping again would just loop. */
export const WIPE_GUARD_WINDOW_MS = 24 * 60 * 60 * 1000

/** Markers older than this are stale (e.g. the user never came back) and are
 *  honored anyway — a wipe of a suspect store is cheap at any age. Kept for
 *  observability: the age is attached to telemetry. */

/**
 * Error shapes observed from a corrupt store's replay. Deliberately narrow:
 * a miss means we fall back to today's behavior (no wipe), a false match
 * costs one benign cache wipe.
 */
const CORRUPTION_SIGNATURES = [
  /Cannot read propert(y|ies) of (null|undefined) \(reading '(length|forEach|constructor)'\)/,
  /(null|undefined) is not an object \(evaluating .*\.(length|forEach)/,
]

function storageKey(prefix: string, projectId: string): string {
  return `${prefix}${projectId}`
}

function readMarker(prefix: string, projectId: string): number | null {
  try {
    const raw = window.localStorage.getItem(storageKey(prefix, projectId))
    if (!raw) return null
    const ts = Number(raw)
    return Number.isFinite(ts) ? ts : null
  } catch {
    return null
  }
}

function writeMarker(prefix: string, projectId: string, now: number): boolean {
  try {
    window.localStorage.setItem(storageKey(prefix, projectId), String(now))
    return true
  } catch {
    return false
  }
}

function clearMarker(prefix: string, projectId: string): void {
  try {
    window.localStorage.removeItem(storageKey(prefix, projectId))
  } catch {
    /* best effort */
  }
}

/** True when an error message matches a known corrupt-replay signature. */
export function matchesCorruptionSignature(message: string | undefined | null): boolean {
  if (typeof message !== 'string' || message.length === 0) return false
  return CORRUPTION_SIGNATURES.some(re => re.test(message))
}

/**
 * Mark a project's local store as corrupt and reload once so the next boot
 * repairs it. The reload guard makes this one-shot per project per window —
 * if we already reloaded for this project recently, just mark and continue
 * (the wipe still happens on whatever the next navigation is).
 */
export function markLocalStoreCorrupt(projectId: string, error: unknown, source: string, now = Date.now()): void {
  const alreadyMarked = readMarker(CORRUPT_MARKER_PREFIX, projectId) !== null
  writeMarker(CORRUPT_MARKER_PREFIX, projectId, now)
  if (alreadyMarked) return

  captureSentryError(error instanceof Error ? error : new Error(String(error)), {
    operation: 'corruptStoreRecovery.detected',
    level: 'warning',
    tags: { source: 'corrupt-store-recovery', detection_source: source },
    extra: { projectId },
  })
  trackCanvasLocalDocReset({ projectId, phase: 'detected' })

  const lastReload = readMarker(RELOAD_GUARD_PREFIX, projectId)
  if (lastReload !== null && now - lastReload < WIPE_GUARD_WINDOW_MS) return
  if (!writeMarker(RELOAD_GUARD_PREFIX, projectId, now)) return
  window.location.reload()
}

/**
 * Repair step — call BEFORE constructing IndexeddbPersistence. If the
 * project was marked corrupt (and the wipe guard allows), deletes the local
 * database so bootstrap starts from a fresh store and the server doc.
 * Never throws; never blocks bootstrap for more than ~2s.
 */
export async function wipeLocalStoreIfMarkedCorrupt(projectId: string, now = Date.now()): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const markedAt = readMarker(CORRUPT_MARKER_PREFIX, projectId)
  if (markedAt === null) return false

  const lastWipe = readMarker(WIPE_GUARD_PREFIX, projectId)
  if (lastWipe !== null && now - lastWipe < WIPE_GUARD_WINDOW_MS) {
    // Wiped recently and corrupt again — a wipe is not the remedy. Clear the
    // marker so we stop trying, and surface it.
    clearMarker(CORRUPT_MARKER_PREFIX, projectId)
    captureSentryError(new Error('Local Yjs store corrupt again within the wipe-guard window'), {
      operation: 'corruptStoreRecovery.wipeGuardHit',
      level: 'warning',
      tags: { source: 'corrupt-store-recovery' },
      extra: { projectId, markedAt, lastWipe },
    })
    return false
  }

  const deleted = await deleteProjectDatabase(projectId)
  if (!deleted) {
    // Blocked (another tab), errored, or timed out — keep the corrupt
    // marker so the next boot retries once the other tab is gone.
    return false
  }
  clearMarker(CORRUPT_MARKER_PREFIX, projectId)

  writeMarker(WIPE_GUARD_PREFIX, projectId, now)
  trackCanvasLocalDocReset({ projectId, phase: 'wiped' })
  captureSentryError(new Error('Wiped corrupt local Yjs store; resyncing from server'), {
    operation: 'corruptStoreRecovery.wiped',
    level: 'info',
    tags: { source: 'corrupt-store-recovery' },
    extra: { projectId, markedAgeMs: now - markedAt },
  })
  return true
}

/**
 * Delete `martini-${projectId}` from IndexedDB. Resolves false on error,
 * on `blocked` (another tab holds a connection — deleting under it would
 * hang forever), or after a 2s safety timeout.
 */
export function deleteProjectDatabase(projectId: string): Promise<boolean> {
  return new Promise(resolve => {
    let settled = false
    const settle = (ok: boolean) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(ok)
    }
    const timer = setTimeout(() => settle(false), 2000)
    try {
      const request = window.indexedDB.deleteDatabase(`martini-${projectId}`)
      request.onsuccess = () => settle(true)
      request.onerror = () => settle(false)
      request.onblocked = () => settle(false)
    } catch {
      settle(false)
    }
  })
}

/**
 * Install detection around a just-constructed IndexeddbPersistence.
 *
 * Covers the three ways a corrupt replay is known to surface:
 *  - a `whenSynced` rejection (promise-chain failures)
 *  - the persistence 'error' event
 *  - an uncaught window error / unhandled rejection matching the corruption
 *    signatures while the store is still syncing (production stacks are
 *    minified, so the module can't be identified by frame — the signature
 *    match plus the sync-window scoping keeps this narrow)
 *
 * Returns a cleanup function; call it once the store has synced (plus a
 * small grace) or on bootstrap teardown.
 */
export function installCorruptStoreDetection(
  projectId: string,
  persistence: {
    whenSynced?: Promise<unknown>
    on: (event: string, cb: (arg: Error) => void) => void
    off?: (event: string, cb: (arg: Error) => void) => void
  },
): () => void {
  // Detection is scoped to the initial sync window. After cleanup every
  // handler is inert — a later quota/transaction error on a cleanly-synced
  // store is not replay corruption and must not trigger a mark + reload.
  let active = true
  const onCorrupt = (error: unknown, source: string) => {
    if (!active) return
    markLocalStoreCorrupt(projectId, error, source)
  }

  const onPersistenceError = (err: Error) => onCorrupt(err, 'persistence-error')
  persistence.whenSynced?.catch(err => onCorrupt(err, 'whenSynced'))
  persistence.on('error', onPersistenceError)

  const onWindowError = (event: ErrorEvent) => {
    if (matchesCorruptionSignature(event.error?.message ?? event.message)) {
      onCorrupt(event.error ?? new Error(event.message), 'window-error')
    }
  }
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason as { message?: string } | undefined
    if (matchesCorruptionSignature(reason?.message)) {
      onCorrupt(event.reason, 'unhandled-rejection')
    }
  }
  window.addEventListener('error', onWindowError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)

  return () => {
    active = false
    persistence.off?.('error', onPersistenceError)
    window.removeEventListener('error', onWindowError)
    window.removeEventListener('unhandledrejection', onUnhandledRejection)
  }
}
